import { useMemo } from 'react';

import {
  type DEFAULT_AD_FILTER_PARAMS,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../shared/constants';
import {
  useGetAccountsMutation,
  useGetApplicationEndpointsMutation,
  useGetEntitlementsMutation,
  useGetRisksMutation,
  useGetUsersMutation,
  useLazyGetBusinessProcessQuery,
  useLazyGetEntitlementTypesQuery,
  useLazyGetFunctionsQuery,
  useLazyGetMitiatingControlsQuery,
  useLazyGetRulesetsQuery,
} from '../data-layer/violationsTabApi';

interface UnwrappablePromise<T> extends Promise<T> {
  abort(): void;
  unwrap(): Promise<T>;
}

type QueryMap = Record<
  string,
  (params: typeof DEFAULT_AD_FILTER_PARAMS) => UnwrappablePromise<unknown>
>;

const EMPTY_QUERY_FN: QueryMap = {
  EMPTY_QUERY_FN_KEY: () =>
    ({
      unwrap() {
        return Promise.resolve('empty_query_fn_run_for_query');
      },
    }) as UnwrappablePromise<unknown>,
};

/**
 * Custom hook to generate a query map for advanced filters.
 *
 * This hook retrieves query functions for different advanced filters and
 * returns a memoized object mapping filter keys to their corresponding query functions.
 *
 * @param {boolean} [strict=false] - If `true`, only includes the query function which has initial options;
 *                                   if `false`, includes all types of query functions.
 * @returns {Object} Query map where keys are filter values and values are query functions.
 */
const useGetQueryMap = (strict = false) => {
  const [fetchRiskFilters] = useGetRisksMutation();
  const [fetchBusinessProcess] = useLazyGetBusinessProcessQuery();
  const [fetchAccountFilters] = useGetAccountsMutation();
  const [fetchApplicationEndpoints] = useGetApplicationEndpointsMutation();
  const [fetchUserFilters] = useGetUsersMutation();
  const [fetchRulesetFilters] = useLazyGetRulesetsQuery();
  const [fetchFuncFilters] = useLazyGetFunctionsQuery();
  const [fetchEntitlements] = useGetEntitlementsMutation();
  const [fetchEntitlementTypes] = useLazyGetEntitlementTypesQuery();
  const [fetchMitiatingControls] = useLazyGetMitiatingControlsQuery();

  const queryMap = useMemo(() => {
    /**
     * Eager filters are the ones we need before the page becomes interactive.
     * Avoid stuffing heavyweight lookups (e.g. Applications) in here because
     * the Violations landing route waits for these promises to resolve.
     */
    const eagerFilterQueryFns = {
      [VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value]: fetchRulesetFilters,
    };
    const fullQueryFns = {
      ...eagerFilterQueryFns,
      [VIOLATIONS_TAB_LIST_AD_FILTER.APPLICATION.value]:
        fetchApplicationEndpoints,
    };
    const isEmptyQueryMap = Boolean(
      Object.keys(eagerFilterQueryFns).length === 0
    );

    if (strict && isEmptyQueryMap) {
      return EMPTY_QUERY_FN;
    }

    if (strict) {
      return eagerFilterQueryFns as QueryMap;
    }

    return {
      ...fullQueryFns,
      [VIOLATIONS_TAB_LIST_AD_FILTER.RISKNAME.value]: fetchRiskFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.BUSINESSPROCESS.value]:
        fetchBusinessProcess,
      [VIOLATIONS_TAB_LIST_AD_FILTER.NAME.value]: fetchAccountFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ACCOUNTTYPE.value]: fetchAccountFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.RULESET.value]: fetchRulesetFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.FUNCTION.value]: fetchFuncFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.USERNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.FIRSTNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.LASTNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.SYSTEMUSERNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.EMPLOYEEID.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.COSTCENTER.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.JOBCODE.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.EMAIL.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.DISPLAYNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.DEPARTMENTNAME.value]: fetchUserFilters,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENT_VALUE.value]:
        fetchEntitlements,
      [VIOLATIONS_TAB_LIST_AD_FILTER.ENTITLEMENTTYPE.value]:
        fetchEntitlementTypes,
      [VIOLATIONS_TAB_LIST_AD_FILTER.MITIGATINGCTRLNAME.value]:
        fetchMitiatingControls,
    };
  }, [
    strict,
    fetchRiskFilters,
    fetchBusinessProcess,
    fetchAccountFilters,
    fetchApplicationEndpoints,
    fetchUserFilters,
    fetchRulesetFilters,
    fetchFuncFilters,
    fetchEntitlements,
    fetchEntitlementTypes,
    fetchMitiatingControls,
  ]);

  return queryMap;
};

// eslint-disable-next-line import/prefer-default-export
export { useGetQueryMap };
